import { useState, useEffect, useRef, useCallback } from "react";
import Grid from "@mui/material/Grid";
import Card from "@mui/material/Card";
import Icon from "@mui/material/Icon";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import DialogActions from "@mui/material/DialogActions";
import MenuItem from "@mui/material/MenuItem";
import Checkbox from "@mui/material/Checkbox";
import FormControlLabel from "@mui/material/FormControlLabel";
import Autocomplete from "@mui/material/Autocomplete";
import TextField from "@mui/material/TextField";
import Chip from "@mui/material/Chip";
import swal from "sweetalert";
import { MapContainer, TileLayer, Marker, Tooltip, useMap } from "react-leaflet";
import L from "leaflet";
import "leaflet/dist/leaflet.css";
import axios from "axios";

import MDBox from "admin/components/MDBox";
import MDTypography from "admin/components/MDTypography";
import MDInput from "admin/components/MDInput";
import MDButton from "admin/components/MDButton";
import DashboardLayout from "admin/examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "admin/examples/Navbars/DashboardNavbar";

const API_URL = process.env.REACT_APP_APIHOST || "http://localhost:3033";
const COUNTRIES = ["Syria","Lebanon","Palestine","Jordan","Iraq","Yemen","Egypt","Saudi Arabia","UAE","Kuwait","Bahrain","Qatar","Oman","Turkey","Iran","Afghanistan","Pakistan","Libya","Tunisia","Algeria","Morocco","Sudan","Somalia","Mali","Niger","Burkina Faso","Nigeria","Chad","Mauritania","South Africa","Ethiopia","Kenya","Cameroon","DR Congo","Central African Republic","Mozambique","Senegal","Ivory Coast"];

function AdminReports() {
    const [reports, setReports] = useState([]);
    const [loading, setLoading] = useState(true);
    const [modalOpen, setModalOpen] = useState(false);
    const [editReport, setEditReport] = useState(null);
    const [form, setForm] = useState({ title: "", countries: [], date: "", content: "", images: [], status: "Draft", categories: ['Intelligence'], pushToTelegram: false, pushToPaidTelegram: false });
    const [uploading, setUploading] = useState(false);
    const [reportTab, setReportTab] = useState(0);
    const [markerSearch, setMarkerSearch] = useState('');
    const [searchingLoc, setSearchingLoc] = useState(false);
    const [locSuggestions, setLocSuggestions] = useState([]);
    const searchTimeout = useRef(null);
    const fileRef = useRef(null);

    useEffect(() => { fetchReports(); }, []);

    const fetchReports = async () => {
        try {
            setLoading(true);
            const res = await axios.get(`${API_URL}/reports`);
            if (res.data.status) setReports(res.data.data);
        } catch (e) { console.error(e); }
        finally { setLoading(false); }
    };

    const openAdd = () => {
        const now = new Date();
        const pad = n => String(n).padStart(2, '0');
        setEditReport(null);
        setForm({ title: "", countries: [], categories: ['Intelligence'], date: `${now.getFullYear()}-${pad(now.getMonth()+1)}-${pad(now.getDate())}T${pad(now.getHours())}:${pad(now.getMinutes())}`, content: "", images: [], markers: [], status: "Draft", pushToTelegram: false, pushToPaidTelegram: false });
        setReportTab(0); setModalOpen(true);
    };

    const openEdit = (r) => {
        setEditReport(r);
        setForm({
            title: r.title, categories: r.categories || ['Intelligence'], countries: r.countries || [], content: r.content || "",
            date: r.date ? new Date(r.date).toISOString().slice(0, 16) : "",
            images: r.images || [], markers: r.markers || [], status: r.status || "Draft",
            pushToTelegram: false, pushToPaidTelegram: false,
        });
        setReportTab(0); setModalOpen(true);
    };

    const handleSave = async () => {
        if (!form.title || !form.date) return swal("Error", "Title and date required", "error");
        // Pushing to Telegram requires a published report — auto-publish if a push box is ticked
        const payload = (form.pushToTelegram || form.pushToPaidTelegram) ? { ...form, status: "Published" } : form;
        try {
            if (editReport) {
                await axios.put(`${API_URL}/reports/${editReport._id}`, payload);
            } else {
                await axios.post(`${API_URL}/reports`, payload);
            }
            const pushed = form.pushToTelegram || form.pushToPaidTelegram;
            swal("Success", pushed ? "Report published and pushed to Telegram" : (editReport ? "Report updated" : "Report created"), "success");
            setModalOpen(false); fetchReports();
        } catch (e) { swal("Error", "Failed to save", "error"); }
    };

    const handleDelete = (id) => {
        swal({ title: "Delete?", text: "This cannot be undone", icon: "warning", buttons: true, dangerMode: true })
        .then(async (ok) => {
            if (ok) {
                await axios.delete(`${API_URL}/reports/${id}`);
                swal("Deleted", "", "success"); fetchReports();
            }
        });
    };

    const handleUpload = async (e) => {
        const files = Array.from(e.target.files);
        if (!files.length) return;
        setUploading(true);
        for (const file of files) {
            try {
                const fd = new FormData(); fd.append("image", file);
                const token = localStorage.getItem("token");
                const res = await axios.post(`${API_URL}/upload`, fd, { headers: { "Content-Type": "multipart/form-data", Authorization: `Bearer ${token}` } });
                if (res.data.status) {
                    setForm(prev => ({ ...prev, images: [...prev.images, { url: res.data.data.url, filename: res.data.data.filename, originalName: res.data.data.originalName, label: "" }] }));
                }
            } catch (err) { console.error(err); }
        }
        setUploading(false);
        if (fileRef.current) fileRef.current.value = "";
    };

    const onMarkerSearchChange = (val) => {
        setMarkerSearch(val);
        if (searchTimeout.current) clearTimeout(searchTimeout.current);
        if (val.trim().length < 3) { setLocSuggestions([]); return; }
        searchTimeout.current = setTimeout(async () => {
            try {
                const res = await fetch("https://nominatim.openstreetmap.org/search?format=json&limit=8&q=" + encodeURIComponent(val));
                const data = await res.json();
                setLocSuggestions(data || []);
            } catch(e) { setLocSuggestions([]); }
        }, 400);
    };

    const selectSuggestion = (s) => {
        setForm(prev => ({ ...prev, markers: [...prev.markers, { lat: parseFloat(s.lat), lng: parseFloat(s.lon), label: s.display_name.split(",")[0] }] }));
        setMarkerSearch("");
        setLocSuggestions([]);
    };

    const searchMarkerLocation = async () => {
        if (!markerSearch.trim()) return;
        setSearchingLoc(true);
        try {
            const res = await fetch("https://nominatim.openstreetmap.org/search?format=json&q=" + encodeURIComponent(markerSearch));
            const data = await res.json();
            if (data && data.length > 0) {
                const { lat, lon, display_name } = data[0];
                setForm(prev => ({ ...prev, markers: [...prev.markers, { lat: parseFloat(lat), lng: parseFloat(lon), label: display_name.split(",")[0] }] }));
                setMarkerSearch("");
            }
        } catch (e) { console.error(e); }
        finally { setSearchingLoc(false); }
    };

    const addManualMarker = () => {
        const lat = prompt("Latitude:");
        const lng = prompt("Longitude:");
        const label = prompt("Label:");
        if (lat && lng) {
            setForm(prev => ({ ...prev, markers: [...prev.markers, { lat: parseFloat(lat), lng: parseFloat(lng), label: label || "" }] }));
        }
    };

    const removeMarker = (i) => setForm(prev => ({ ...prev, markers: prev.markers.filter((_, idx) => idx !== i) }));

    const removeImage = (i) => setForm(prev => ({ ...prev, images: prev.images.filter((_, idx) => idx !== i) }));
    const updateImageLabel = (i, label) => setForm(prev => ({ ...prev, images: prev.images.map((img, idx) => idx === i ? { ...img, label } : img) }));

    return (
        <DashboardLayout>
            <DashboardNavbar />
            <MDBox pt={2} pb={2}>
                <Grid container spacing={2}>
                    <Grid item xs={12}>
                        <Card>
                            <MDBox px={2} pt={1} pb={0.5} display="flex" justifyContent="space-between" alignItems="center">
                                <MDTypography variant="body2" sx={{ color: "#C29717", fontSize: "11px", textTransform: "uppercase", letterSpacing: "1.5px" }}>
                                    Reports
                                </MDTypography>
                                <MDTypography component="span" sx={{ color: "#aaa", fontSize: "12px", cursor: "pointer", display: "flex", alignItems: "center", gap: "4px", "&:hover": { color: "#fff" } }} onClick={openAdd}>
                                    <Icon sx={{ fontSize: "14px !important" }}>add_circle_outline</Icon> Add report
                                </MDTypography>
                            </MDBox>

                            <MDBox px={2} py={1}>
                                {loading ? (
                                    <MDBox display="flex" justifyContent="center" p={3}><div className="forte-spin" style={{margin:"0 auto"}}></div></MDBox>
                                ) : reports.length === 0 ? (
                                    <MDTypography sx={{ color: "#888", fontSize: "13px", py: 2, textAlign: "center" }}>No reports yet</MDTypography>
                                ) : (
                                    reports.map(r => (
                                        <MDBox key={r._id} sx={{ borderBottom: "1px solid rgba(255,255,255,0.04)", py: 1.5, display: "flex", justifyContent: "space-between", alignItems: "flex-start" }}>
                                            <MDBox>
                                                <MDTypography sx={{ color: "#fff", fontSize: "13px", fontWeight: 500, mb: 0.5 }}><span style={{color:"#C29717",fontSize:"9px",textTransform:"uppercase",letterSpacing:"1px",marginRight:"8px",opacity:0.8}}>{(r.categories || ["Intelligence"]).join(" / ")}</span>{r.title}</MDTypography>
                                                <MDBox display="flex" gap={0.5} flexWrap="wrap" mb={0.5}>
                                                    {(r.countries || []).map(c => (
                                                        <span key={c} style={{ background: "rgba(194,151,23,0.1)", border: "1px solid rgba(194,151,23,0.2)", borderRadius: "2px", padding: "1px 6px", fontSize: "10px", color: "#C29717" }}>{c}</span>
                                                    ))}
                                                </MDBox>
                                                <MDTypography sx={{ color: "#888", fontSize: "11px" }}>
                                                    {r.date ? new Date(r.date).toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric" }) : ""}
                                                    {r.images && r.images.length > 0 && ` · ${r.images.length} image${r.images.length > 1 ? 's' : ''}`}
                                                    <span style={{ display: "inline-block", width: "6px", height: "6px", borderRadius: "1px", background: r.status === "Published" ? "#4CAF50" : "#555", marginLeft: "8px", verticalAlign: "middle" }}></span>
                                                </MDTypography>
                                            </MDBox>
                                            <MDBox display="flex" gap={0.5}>
                                                <Icon sx={{ fontSize: "16px !important", color: "#888", cursor: "pointer", "&:hover": { color: "#C29717" } }} onClick={() => window.open(API_URL + '/report-view/' + r._id, '_blank')}>download</Icon>
                                                <Icon sx={{ fontSize: "16px !important", color: "#888", cursor: "pointer", "&:hover": { color: "#fff" } }} onClick={() => openEdit(r)}>edit</Icon>
                                                <Icon sx={{ fontSize: "16px !important", color: "#888", cursor: "pointer", "&:hover": { color: "#e55" } }} onClick={() => handleDelete(r._id)}>delete</Icon>
                                            </MDBox>
                                        </MDBox>
                                    ))
                                )}
                            </MDBox>
                        </Card>
                    </Grid>
                </Grid>
            </MDBox>

            <Dialog open={modalOpen} onClose={() => setModalOpen(false)} maxWidth="md" fullWidth
                PaperProps={{ sx: { background: "#1e1e1e", border: "1px solid rgba(255,255,255,0.08)", borderRadius: "2px" } }}>
                <DialogTitle sx={{ borderBottom: "1px solid rgba(255,255,255,0.06)", pb: 1.5 }}>
                    <MDTypography variant="h5" sx={{ color: "#fff !important", fontSize: "14px", textTransform: "uppercase", letterSpacing: "1px" }}>
                        {editReport ? "Edit report" : "Add report"}
                    </MDTypography>
                </DialogTitle>
                <MDBox sx={{ borderBottom: "1px solid rgba(255,255,255,0.06)", px: 3, display: "flex", gap: 0 }}>
                    {["Details", "Content & Media", "Geo markers"].map((t, i) => (
                        <span key={t} onClick={() => setReportTab(i)} style={{
                            padding: "8px 14px", cursor: "pointer", fontSize: "11px", textTransform: "uppercase",
                            letterSpacing: "1px", color: reportTab === i ? "#fff" : "#666",
                            borderBottom: reportTab === i ? "1px solid #C29717" : "1px solid transparent",
                            marginBottom: "-1px"
                        }}>{t}</span>
                    ))}
                </MDBox>
                <DialogContent sx={{ overflow: "visible", minHeight: "450px" }}>
                    <MDBox mt={2}>
                        {reportTab === 0 && <Grid container spacing={2}>
                            <Grid item xs={8}>
                                <MDInput type="text" label="Report title *" fullWidth value={form.title} onChange={e => setForm({ ...form, title: e.target.value })} />
                            </Grid>
                            <Grid item xs={4}>
                                <MDTypography sx={{ color: "#999", fontSize: "11px", mb: 0.5 }}>Categories</MDTypography>
                                <MDBox display="flex" gap={1}>
                                    {['Intelligence', 'Forecast'].map(cat => (
                                        <span key={cat} onClick={() => {
                                            const has = form.categories.includes(cat);
                                            setForm({...form, categories: has ? form.categories.filter(c=>c!==cat) : [...form.categories, cat]});
                                        }} style={{
                                            padding:'4px 10px', fontSize:'11px', cursor:'pointer', borderRadius:'2px',
                                            background: form.categories.includes(cat) ? 'rgba(194,151,23,0.15)' : 'transparent',
                                            border: form.categories.includes(cat) ? '1px solid rgba(194,151,23,0.4)' : '1px solid rgba(255,255,255,0.12)',
                                            color: form.categories.includes(cat) ? '#C29717' : '#888',
                                        }}>{cat}</span>
                                    ))}
                                </MDBox>
                            </Grid>
                            <Grid item xs={12}>
                                <Autocomplete multiple freeSolo options={COUNTRIES} value={form.countries}
                                    onChange={(_, v) => setForm({ ...form, countries: v })}
                                    renderTags={(val, getTagProps) => val.map((c, i) => {
                                        const { onDelete } = getTagProps({ index: i });
                                        return (
                                            <span key={c} style={{display:'inline-flex',alignItems:'center',gap:'4px',background:'rgba(194,151,23,0.15)',border:'1px solid rgba(194,151,23,0.3)',borderRadius:'2px',padding:'2px 8px',fontSize:'11px',color:'#fff',margin:'2px'}}>
                                                {c}
                                                <span style={{cursor:'pointer',color:'#C29717',fontSize:'14px',lineHeight:1}} onClick={onDelete}>&times;</span>
                                            </span>
                                        );
                                    })}
                                    renderInput={(params) => <TextField {...params} label="Country coverage" variant="outlined" size="small" />} />
                            </Grid>
                            <Grid item xs={6}>
                                <TextField type="datetime-local" label="Date *" value={form.date} onChange={e => setForm({ ...form, date: e.target.value })} variant="outlined" fullWidth InputLabelProps={{ shrink: true }} />
                            </Grid>
                            <Grid item xs={6}>
                                <MDInput select label="Status" fullWidth value={form.status} onChange={e => setForm({ ...form, status: e.target.value })}
                                    InputLabelProps={{ shrink: true }} InputProps={{ style: { height: "40px" } }} SelectProps={{ style: { height: "40px" } }} sx={{ height: "40px" }}>
                                    <MenuItem value="Draft">Draft</MenuItem>
                                    <MenuItem value="Published">Published</MenuItem>
                                </MDInput>
                            </Grid>
                            <Grid item xs={12}>
                                <MDBox sx={{ mt: 0.5, p: 1.5, border: "1px solid rgba(255,255,255,0.12)", borderRadius: "4px" }}>
                                    <MDTypography sx={{ color: "#C29717", fontSize: "10px", textTransform: "uppercase", letterSpacing: "1px", mb: 0.5, display: "block" }}>
                                        Telegram push {(form.pushToTelegram || form.pushToPaidTelegram) && form.status !== "Published" ? "— publishes & sends on save" : ""}
                                    </MDTypography>
                                    <FormControlLabel
                                        control={<Checkbox checked={!!form.pushToTelegram} onChange={e => setForm({ ...form, pushToTelegram: e.target.checked })} />}
                                        label={<MDTypography variant="button" color="text">Push to Telegram users (free-trial subscribers of the selected countries)</MDTypography>}
                                    />
                                    <br />
                                    <FormControlLabel
                                        control={<Checkbox checked={!!form.pushToPaidTelegram} onChange={e => setForm({ ...form, pushToPaidTelegram: e.target.checked })} />}
                                        label={<MDTypography variant="button" color="text">Push to paid Telegram users (excludes free trials — for later)</MDTypography>}
                                    />
                                </MDBox>
                            </Grid>
                        </Grid>}
                        {reportTab === 1 && <Grid container spacing={2}>
                            <Grid item xs={12}>
                                <MDTypography sx={{ color: "#999", fontSize: "11px", mb: 0.5 }}>Report content</MDTypography>
                                <MDBox sx={{ border: "1px solid rgba(255,255,255,0.15)", borderRadius: "2px", overflow: "hidden" }}>
                                    <MDBox sx={{ display: "flex", gap: "2px", padding: "4px 6px", borderBottom: "1px solid rgba(255,255,255,0.08)", background: "rgba(255,255,255,0.03)" }}>
                                        <span className="rte-btn" onMouseDown={e => { e.preventDefault(); document.execCommand('bold'); }} title="Bold"><b>B</b></span>
                                        <span className="rte-btn" onMouseDown={e => { e.preventDefault(); document.execCommand('italic'); }} title="Italic"><i>I</i></span>
                                        <span className="rte-btn" onMouseDown={e => { e.preventDefault(); document.execCommand('formatBlock', false, 'h3'); }} title="Title">T</span>
                                        <span className="rte-btn" onMouseDown={e => { e.preventDefault(); document.execCommand('insertUnorderedList'); }} title="Bullet list">&bull;</span>
                                        <span style={{width:'1px',background:'rgba(255,255,255,0.1)',margin:'0 4px'}}></span>
                                        <span className="rte-btn" onMouseDown={e => { e.preventDefault(); document.execCommand('foreColor', false, '#ffffff'); }} title="White"><span style={{width:'10px',height:'10px',background:'#fff',display:'inline-block',borderRadius:'1px'}}></span></span>
                                        <span className="rte-btn" onMouseDown={e => { e.preventDefault(); document.execCommand('foreColor', false, '#C29717'); }} title="Gold"><span style={{width:'10px',height:'10px',background:'#C29717',display:'inline-block',borderRadius:'1px'}}></span></span>
                                        <span className="rte-btn" onMouseDown={e => { e.preventDefault(); document.execCommand('foreColor', false, '#e55555'); }} title="Red"><span style={{width:'10px',height:'10px',background:'#e55',display:'inline-block',borderRadius:'1px'}}></span></span>
                                    </MDBox>
                                    <div
                                        className="rte-editor" style={{whiteSpace:'pre-wrap'}}
                                        contentEditable
                                        dangerouslySetInnerHTML={{ __html: form.content }}
                                        onBlur={e => setForm(prev => ({ ...prev, content: e.target.innerHTML }))}
                                        suppressContentEditableWarning
                                    />
                                </MDBox>
                            </Grid>
                            <Grid item xs={12}>
                                <MDTypography sx={{ color: "#C29717", fontSize: "10px", textTransform: "uppercase", letterSpacing: "1.5px", mb: 1 }}>Images</MDTypography>
                                <input type="file" ref={fileRef} style={{ display: "none" }} accept="image/*" multiple onChange={handleUpload} />
                                <MDBox display="flex" gap={1} flexWrap="wrap" mb={1}>
                                    {form.images.map((img, i) => (
                                        <MDBox key={i} sx={{ border: "1px solid rgba(255,255,255,0.08)", borderRadius: "2px", overflow: "hidden", width: "160px" }}>
                                            <img src={`${window.location.origin}${img.url}`} alt="" style={{ width: "100%", height: "80px", objectFit: "cover" }} />
                                            <MDBox p={0.5}>
                                                <input type="text" placeholder="Label" value={img.label || ""} onChange={e => updateImageLabel(i, e.target.value)}
                                                    style={{ width: "100%", background: "#2a2a2a", border: "1px solid rgba(255,255,255,0.1)", borderRadius: "2px", padding: "3px 6px", fontSize: "11px", color: "#ccc", outline: "none" }} />
                                                <span style={{color:"#e55",cursor:"pointer",fontSize:"13px",marginTop:"4px"}} onClick={() => removeImage(i)}>x</span>
                                            </MDBox>
                                        </MDBox>
                                    ))}
                                    <MDBox sx={{ width: "160px", height: "110px", border: "1px dashed rgba(255,255,255,0.12)", borderRadius: "2px", display: "flex", alignItems: "center", justifyContent: "center", cursor: "pointer", "&:hover": { borderColor: "#C29717" } }}
                                        onClick={() => fileRef.current && fileRef.current.click()}>
                                        <Icon sx={{ color: "#666", fontSize: "24px !important" }}>add_photo_alternate</Icon>
                                    </MDBox>
                                </MDBox>
                            </Grid>
                        </Grid>}
                        {reportTab === 2 && <MDBox>
                            <MDTypography sx={{ color: "#C29717", fontSize: "10px", textTransform: "uppercase", letterSpacing: "1.5px", mb: 1 }}>Add marker</MDTypography>
                            <MDBox display="flex" gap={1} mb={2}>
                                <div style={{ flex: 1, position: "relative" }}>
                                    <input type="text" placeholder="Search location..." value={markerSearch}
                                        onChange={e => onMarkerSearchChange(e.target.value)}
                                        onKeyDown={e => e.key === "Enter" && searchMarkerLocation()}
                                        style={{ width: "100%", background: "#2a2a2a", border: "1px solid rgba(255,255,255,0.15)", borderRadius: "2px", padding: "6px 10px", fontSize: "12px", color: "#fff", outline: "none" }} />
                                    {locSuggestions.length > 0 && (
                                        <div style={{ position: "absolute", top: "100%", left: 0, right: 0, background: "#252525", border: "1px solid rgba(255,255,255,0.1)", borderRadius: "2px", zIndex: 9999, maxHeight: "350px", overflowY: "auto", marginTop: "2px" }}>
                                            {locSuggestions.map((s, i) => (
                                                <div key={i} onClick={() => selectSuggestion(s)} style={{ padding: "8px 10px", fontSize: "11px", color: "#ccc", cursor: "pointer", borderBottom: "1px solid rgba(255,255,255,0.04)" }}
                                                    onMouseEnter={e => e.target.style.background = "rgba(194,151,23,0.1)"}
                                                    onMouseLeave={e => e.target.style.background = "transparent"}>
                                                    {s.display_name}
                                                </div>
                                            ))}
                                        </div>
                                    )}
                                </div>
                                <span onClick={searchMarkerLocation} style={{ padding: "6px 12px", background: "rgba(194,151,23,0.15)", border: "1px solid rgba(194,151,23,0.3)", borderRadius: "2px", color: "#C29717", fontSize: "11px", cursor: "pointer" }}>
                                    {searchingLoc ? "..." : "Search"}
                                </span>
                                <span onClick={addManualMarker} style={{ padding: "6px 12px", background: "rgba(255,255,255,0.05)", border: "1px solid rgba(255,255,255,0.12)", borderRadius: "2px", color: "#aaa", fontSize: "11px", cursor: "pointer" }}>
                                    Lat/Lng
                                </span>
                            </MDBox>
                            {form.markers.length > 0 && (
                                <MDBox mb={2}>
                                    {form.markers.map((mk, i) => (
                                        <MDBox key={i} display="flex" justifyContent="space-between" alignItems="center" sx={{ py: 0.8, borderBottom: "1px solid rgba(255,255,255,0.04)" }}>
                                            <MDBox>
                                                <span style={{ color: "#fff", fontSize: "12px" }}>{mk.label || "Marker " + (i+1)}</span>
                                                <span style={{ color: "#666", fontSize: "10px", marginLeft: "8px" }}>{mk.lat.toFixed(4)}, {mk.lng.toFixed(4)}</span>
                                            </MDBox>
                                            <span style={{ color: "#e55", cursor: "pointer", fontSize: "13px" }} onClick={() => removeMarker(i)}>x</span>
                                        </MDBox>
                                    ))}
                                </MDBox>
                            )}
                            {form.markers.length > 0 && (
                                <MDBox sx={{ height: "350px", borderRadius: "2px", overflow: "hidden", border: "1px solid rgba(255,255,255,0.08)" }}>
                                    <MapContainer
                                        key={"rpt-map-" + form.markers.map(m=>m.lat+","+m.lng).join(";")}
                                        center={[form.markers[0].lat, form.markers[0].lng]}
                                        zoom={6}
                                        style={{ height: "100%", width: "100%" }}
                                        attributionControl={false}
                                    >
                                        <TileLayer url="https://server.arcgisonline.com/ArcGIS/rest/services/Canvas/World_Dark_Gray_Base/MapServer/tile/{z}/{y}/{x}" />
                                        <TileLayer url="https://server.arcgisonline.com/ArcGIS/rest/services/Canvas/World_Dark_Gray_Reference/MapServer/tile/{z}/{y}/{x}" />
                                        {form.markers.map((mk, i) => (
                                            <Marker
                                                key={i + "-" + mk.lat + "-" + mk.lng}
                                                position={[mk.lat, mk.lng]}
                                                draggable={true}
                                                icon={L.divIcon({
                                                    className: "",
                                                    html: '<div style="width:14px;height:14px;border-radius:50%;background:#e55;border:2px solid rgba(255,255,255,0.5);cursor:grab;transform:translate(-7px,-7px)"></div>',
                                                    iconSize: [0, 0], iconAnchor: [0, 0],
                                                })}
                                                eventHandlers={{
                                                    dragend: (e) => {
                                                        const ll = e.target.getLatLng();
                                                        setForm(prev => ({
                                                            ...prev,
                                                            markers: prev.markers.map((m, idx) =>
                                                                idx === i ? { ...m, lat: parseFloat(ll.lat.toFixed(5)), lng: parseFloat(ll.lng.toFixed(5)) } : m
                                                            )
                                                        }));
                                                    },
                                                }}
                                            >
                                                {mk.label && (
                                                    <Tooltip permanent direction="right" offset={[10, 0]}>
                                                        <span style={{ background: "rgba(26,26,26,0.85)", border: "1px solid rgba(229,85,85,0.4)", color: "#e55", fontSize: "10px", padding: "1px 5px", borderRadius: "2px" }}>
                                                            {mk.label}
                                                        </span>
                                                    </Tooltip>
                                                )}
                                            </Marker>
                                        ))}
                                    </MapContainer>
                                </MDBox>
                            )}
                        </MDBox>}
                    </MDBox>
                </DialogContent>
                <DialogActions sx={{ borderTop: "1px solid rgba(255,255,255,0.06)", pt: 1.5 }}>
                    <MDButton onClick={() => setModalOpen(false)} variant="outlined" color="secondary">Cancel</MDButton>
                    <MDButton onClick={handleSave} variant="gradient" color="info">Save</MDButton>
                </DialogActions>
            </Dialog>
        </DashboardLayout>
    );
}

export default AdminReports;
